import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';


type TranslationFunction = (key: string, value: string) => string;

type LoaderTexts = {
  jdTexts: string[];
  prescreeningTexts: string[];
  resumeTexts: string[];
  messagingTexts: string[];
  engageContentTexts: string[];
};

export const PAGE_SIZE = 10;

export const getPromptsList = (t: TranslationFunction): Array<string> => [
  `${t('Empathetic_and_Supportive', 'Empathetic and Supportive')}`,
  `${t('Friendly_and_Conversational', 'Friendly and Conversational')}`,
  `${t('Professional_and_Formal', 'Professional and Formal')}`,
  `${t('Gender_Neutral', 'Gender Neutral')}`,
];

export const getAddPromptTypes = (t: TranslationFunction): Array<string> => [
  `${t('skills', 'skills')}`,
  `${t('responsibilities', 'responsibilities')}`,
];
export const getTonePromptTypes = (t: TranslationFunction): Array<string> => [
  `${t('missingSections', 'missingSections')}`,
  `${t('contentEnhancement', 'contentEnhancement')}`,
];

export const getIssueTypes = (t: TranslationFunction): Array<MenuOption> => [
  {
    key: '1',
    label: `${t('The_content_wasnt_relevant', 'The content wasn’t relevant')}`,
  },
  {
    key: '2',
    label: `${t('The_content_wasnt_logical', 'The content wasn’t logical')}`,
  },
  {
    key: '3',
    label: `${t(
      'The_content_has_factual_inaccuracies',
      'The content has factual inaccuracies',
    )}`,
  },
  {
    key: '4',
    label: `${t(
      'The_content_has_poor_language_fluency',
      'The content has poor language fluency',
    )}`,
  },
];

export const QuestionEnhanceMentOptions = [
  {
    disabled: true,
    key: 'Enhance with AI',
    label: 'Enhance with AI',
  },
  {
    key: 'Shorter',
    label: 'Shorter',
  },
  {
    key: 'Longer',
    label: 'Longer',
  },
  {
    key: 'Grammar Check',
    label: 'Grammar Check',
  },
  {
    disabled: true,
    key: 'More Options',
    label: 'More Options',
  },
  {
    key: 'Edit',
    label: 'Edit',
  },
  {
    key: 'Remove',
    label: 'Remove',
  },
] as const;

export const QuestionSetEnhanceMentOptions = [
  {
    disabled: true,
    key: 'Enhance with AI',
    label: 'Enhance with AI',
  },
  {
    key: 'Shorter',
    label: 'Shorter',
  },
  {
    key: 'Longer',
    label: 'Longer',
  },
  {
    key: 'Grammar Check',
    label: 'Grammar Check',
  },
  {
    disabled: true,
    key: 'Change tone',
    label: 'Change tone',
  },
  {
    key: 'Friendly',
    label: 'Friendly',
  },
  {
    key: 'Professional',
    label: 'Professional',
  },
  {
    key: 'Casual',
    label: 'Casual',
  },
] as const;

export const STRING_CONSTANTS = {
  HOME_PAGE_TITLE: 'AI Content Generator',
  HOME_PAGE_SUBTEXT:
    'Access the power of AI, right inside Sense. Select your project below to get started.',
  AI_INFORMATION_TEXT:
    'IMPORTANT: We recommend reviewing AI-generated content for accuracy and appropriateness.',
  CHATBOT_GENERATED_FEEDBACK:
    'Your prescreening chatbot has been successfully generated.',
  PRE_SCREENING_QUESTION_SAVE:
    'Your prescreening questions have been successfully saved.',
} as const;

export const ERROR_TEXTS = {
  SAVE_RESUME_ERROR:
    'Error downloading the resume, please try again after sometime.',
  EMPTY_PRE_SCREENING_TABLE:
    'You don’t have any screening questions created. Get started by creating one.',
  EMPTY_RESUME_TABLE:
    'You haven\'t created any resumes yet. Click Create New to start.',
  NO_JOBS_ASSIGNED: 'You do not have any jobs assigned to you currently.',
  NO_JD_CREATED:
    'You don’t have any job descriptions created. Get started by creating one.',
  CANDIDATES_SEARCH_ERROR: 'Error Searching for candidates',
  NO_RESPONSE_CHATBOT: 'There has been no response to the selected chatbot.',
  NO_CHATBOTS_CREATED:
    'No pre-screening chatbots are created, please create one to get started',
} as const;

export const CARDS_TITLE = {
  JOB_DESCRIPTION_GENERATOR: 'Job Descriptions',
  PRE_SCREENING: 'Pre-screening Questions',
  RESUME_GENERATOR: 'Resumes',
  SURVEY_SUMMARY: 'Survey Summaries',
} as const;

export const CARDS = [
  {
    iconName: 'file-signature',
    title: CARDS_TITLE.JOB_DESCRIPTION_GENERATOR,
    description:
      'Generate complete job descriptions in seconds with just a job title and brief summary',
  },
  {
    iconName: 'comments',
    title: CARDS_TITLE.PRE_SCREENING,
    description:
      'Instantly create pre-screening questions based on job titles or descriptions',
  },
  {
    iconName: 'file',
    title: CARDS_TITLE.RESUME_GENERATOR,
    description:
      'Automatically compose candidate resumes based on new information or completed chatbot conversations',
  },
  {
    iconName: 'square-poll-vertical',
    title: CARDS_TITLE.SURVEY_SUMMARY,
    description:
      'Automatically generate survey summary based on journey, touchpoints and questions',
  },
] as const;

export const getLoaderTexts = (t: TranslationFunction): LoaderTexts => ({
  jdTexts: [
    `${t('Cooking_Up_Custom_Careers', 'Cooking Up Custom Careers')}...${t(
      'Stirring_Up_the_Perfect_Job_Recipe',
      'Stirring Up the Perfect Job Recipe',
    )}`,
    `${t(
      'Spinning_Syllables_into_Dream_Jobs',
      'Spinning Syllables into Dream Jobs',
    )}...${t(
      'Unleashing_the_Job_Description_Djinn',
      'Unleashing the Job Description Djinn',
    )}`,
  ],
  prescreeningTexts: [
    `${t(
      'Polishing_Interview_Crystal_Balls',
      'Polishing Interview Crystal Balls',
    )}...${t(
      'Conjuring_Questions_from_the_HR_Universe',
      'Conjuring Questions from the HR Universe',
    )}`,
    `${t(
      'Summoning_Smart_Queries_for_You',
      'Summoning Smart Queries for You',
    )}...${t(
      'Crafting_Tailored_Questions_for_Candidates',
      'Crafting Tailored Questions for Candidates',
    )}`,
  ],
  resumeTexts: [
    `${t(
      'Crafting_Resumes_Pixel_by_Pixel',
      'Crafting Resumes, Pixel by Pixel',
    )}...${t(
      'Turning_Words_into_Professional_Portraits',
      'Turning Words into Professional Portraits',
    )}`,
    `${t(
      'Building_Career_Chronicles_Bit_by_Bit',
      'Building Career Chronicles, Bit by Bit',
    )}...${t(
      'Weaving_Professional_Stories_from_Text',
      'Weaving Professional Stories from Text',
    )}`,
    `${t(
      'Unfolding_the_Resume_Red_Carpet',
      'Unfolding the Resume Red Carpet',
    )}...${t(
      'Breathing_Life_into_Words_on_Resumes',
      'Breathing Life into Words on Resumes',
    )}`,
  ],
  messagingTexts: [
    `${t(
      'Summoning_Words_Crafting_Spells',
      'Summoning Words, Crafting Spells',
    )}...${t(
      'Weaving_Words_for_the_Recruiters_Quill',
      'Weaving Words for the Recruiter’s Quill',
    )}`,
    `${t(
      'Crafting_Conversations_with_Code',
      'Crafting Conversations with Code',
    )}...${t(
      'Blending_Syntax_and_Emotion_into_Messages',
      'Blending Syntax and Emotion into Messages',
    )}`,
  ],
  engageContentTexts: [
    `${t(
      'Summoning_Words_Crafting_Spells',
      'Summoning Words, Crafting Spells',
    )}...${t(
      'Weaving_Words_for_the_Recruiters_Quill',
      'Weaving Words for the Recruiter’s Quill',
    )}`,
    `${t(
      'Crafting_Conversations_with_Code',
      'Crafting Conversations with Code',
    )}...${t(
      'Blending_Syntax_and_Emotion_into_Messages',
      'Blending Syntax and Emotion into Messages',
    )}`,
  ],
});

export const SENSE_AI_MODULE = {
  ENGAGE_EMAIL: 'Engage Email',
  ENGAGE_SMS: 'Engage SMS',
  MESSAGING: 'Messaging',
} as const;

export const SENSE_PLATFORM = {
  WEB: 'Web',
  CHROME_EXTENSION: 'Chrome Extension',
} as const;
